import { useState, useRef } from "react";
import { api } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileSpreadsheet, Loader2, Calendar, Upload, FileUp } from "lucide-react";
import { toast } from "sonner";

const AdminExports = () => {
  const [exporting, setExporting] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: ""
  });

  const exportTypes = [
    { id: 'customers', label: 'Customers', description: 'Export all customer data including profile and loyalty info' },
    { id: 'rides', label: 'Rides', description: 'Export all ride records with points earned' },
    { id: 'vouchers', label: 'Vouchers', description: 'Export all voucher codes and their status' },
    { id: 'events', label: 'Events', description: 'Export activity events and logs' }
  ];

  const handleExport = async (dataType) => {
    setExporting(dataType);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await api.get(`/admin/export/${dataType}?${params.toString()}`);
      
      if (response.data.csv && response.data.count > 0) {
        // Create and download CSV file
        const blob = new Blob([response.data.csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${dataType}_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`Exported ${response.data.count} ${dataType}`);
      } else {
        toast.info('No data to export');
      }
    } catch (error) {
      toast.error(`Failed to export ${dataType}`);
    } finally {
      setExporting(null);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    try {
      const csvContent = await file.text();
      const response = await api.post('/admin/import/rides', csvContent, {
        headers: { 'Content-Type': 'text/plain' }
      });
      
      if (response.data.success) {
        toast.success(`Successfully imported ${response.data.imported} rides`);
        if (response.data.errors.length > 0) {
          console.warn('Import errors:', response.data.errors);
          toast.warning(`${response.data.errors.length} rows had errors (check console)`);
        }
      } else {
        toast.error('Import failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import rides');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <AdminLayout title="Data Exports">
      <div className="space-y-6 animate-fade-in">
        {/* Date Filters */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar size={20} className="text-[#D4AF37]" />
              Date Range (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  data-testid="export-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  data-testid="export-end-date"
                />
              </div>
            </div>
            {(filters.start_date || filters.end_date) && (
              <Button
                variant="link"
                onClick={() => setFilters({ start_date: '', end_date: '' })}
                className="text-[#A0A0A0] hover:text-white p-0 mt-2"
              >
                Clear dates
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Export Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {exportTypes.map((type) => (
            <Card key={type.id} className="bg-[#131316] border-[#2A2A2F]">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                        <FileSpreadsheet size={20} className="text-[#D4AF37]" />
                      </div>
                      <h3 className="text-white font-semibold">{type.label}</h3>
                    </div>
                    <p className="text-[#A0A0A0] text-sm mb-4">{type.description}</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleExport(type.id)}
                  disabled={exporting === type.id}
                  className="w-full bg-[#1A1A1E] border border-[#2A2A2F] text-white hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  data-testid={`export-${type.id}-btn`}
                >
                  {exporting === type.id ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting...</>
                  ) : (
                    <><Download size={16} className="mr-2" /> Export CSV</>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Import Section */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Upload size={20} className="text-[#D4AF37]" />
              Import Rides
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-[#A0A0A0] text-sm">
              Upload a CSV file to import ride data. The CSV must include these columns: user_id, vehicle_type, distance_miles, fare_cents, ride_date (optional).
            </p>
            <div className="border-2 border-dashed border-[#2A2A2F] rounded-lg p-8 text-center hover:border-[#D4AF37] transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImport}
                disabled={importing}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer block">
                <div className="mx-auto w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                  <FileUp size={24} className="text-[#D4AF37]" />
                </div>
                <p className="text-white font-medium mb-2">
                  {importing ? 'Importing...' : 'Click to upload CSV'}
                </p>
                <p className="text-[#6B6B6B] text-sm">
                  {importing ? 'Processing your file...' : 'Drag and drop or click to browse'}
                </p>
              </label>
            </div>
            <div className="bg-[#0A0A0B] border border-[#2A2A2F] rounded-lg p-4">
              <p className="text-[#D4AF37] text-xs font-medium mb-2">CSV Format Example:</p>
              <code className="text-[#A0A0A0] text-xs block">
                user_id,vehicle_type,distance_miles,fare_cents,ride_date<br/>
                abc123,luxury,15.5,8500,2025-01-01T10:00:00
              </code>
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="bg-[#0A0A0B] border-[#2A2A2F]">
          <CardContent className="p-4">
            <p className="text-[#6B6B6B] text-sm">
              Exports are generated as CSV files. Large exports may take a few moments to process.
              Date filters apply to the created_at field of each record.
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminExports;
